import React, { useEffect, useState } from "react";
import { Pagination } from "./Pagination";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { Server, RefreshCw, Search, ChevronUp, ChevronDown } from "lucide-react";

interface ServiceListProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
}

const DEFAULT_PER_PAGE = 10;

const TruncatedPath: React.FC<{ path?: string }> = ({ path }) => {
    const [expanded, setExpanded] = useState(false);

    if (!path) return <span className="text-gray-400">-</span>;

    const isLong = path.length > 30;

    if (!isLong) {
        return <span className="font-mono text-gray-300 break-all text-[10px]">{path}</span>;
    }

    return (
        <div className="font-mono text-[10px] text-gray-300">
            <div
                className={expanded ? "whitespace-normal break-all bg-gray-800/90 p-1.5 rounded border border-gray-700 my-0.5 text-[10px] leading-relaxed text-gray-200 select-text" : "truncate"}
                title={path}
            >
                {path}
            </div>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(!expanded);
                }}
                className="text-[9px] font-semibold text-yellow-400 hover:text-yellow-300 hover:underline mt-0.5 inline-block cursor-pointer focus:outline-none"
            >
                {expanded ? "Show less" : "Show more"}
            </button>
        </div>
    );
};

const ServiceList: React.FC<ServiceListProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
}) => {
    const [services, setServices] = useState<any[]>([]);
    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
    const [total, setTotal] = useState<number>(0);
    const [search, setSearch] = useState<string>("");
    const [searchInput, setSearchInput] = useState<string>("");
    const [loading, setLoading] = useState(true);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    const fetchServices = async (
        skip: number = 0,
        limit: number = perPage,
        searchTerm: string = search,
        currentSort = sortConfig
    ) => {
        setLoading(true);
        try {
            const queryObj: any = { client_id, organization_id, agent_id, asset_id };
            if (searchTerm) { queryObj.$or = [{ display_name: { $ilike: `%${searchTerm}%` } }, { status: { $ilike: `%${searchTerm}%` } }, { name: { $ilike: `%${searchTerm}%` } }, { description: { $ilike: `%${searchTerm}%` } }, { status: { $ilike: `%${searchTerm}%` } }, { start_type: { $ilike: `%${searchTerm}%` } }, { service_account: { $ilike: `%${searchTerm}%` } }, { process_name: { $ilike: `%${searchTerm}%` } }, { binary_path: { $ilike: `%${searchTerm}%` } }]; }

            const params: any = {
                skip,
                limit,
                query: JSON.stringify(queryObj),
            };

            // Add API Sorting if requested
            if (currentSort) {
                params.sort_by = currentSort.key;
                params.sort_order = currentSort.direction;
            }

            const response = await baseapi.get("/asset_services", { params });
            setServices(response.data?.data ?? []);
            setTotal(response.data?.total ?? 0);
        } catch (err: any) {
            showError(err.message);
            setServices([]);
            setTotal(0);
        }
        setLoading(false);
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setSearch(searchInput);
        }, 400);
        return () => clearTimeout(handler);
    }, [searchInput]);

    useEffect(() => {
        setPage(1);
        fetchServices(0, perPage, search, sortConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client_id, organization_id, agent_id, asset_id, search]);

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            const trimmedSearch = searchInput.trim();
            setSearch(trimmedSearch);
            setPage(1);
            fetchServices(0, perPage, trimmedSearch, sortConfig);
        }
    };

    // const handleSearchButton = () => {
    //     const trimmedSearch = searchInput.trim();
    //     setSearch(trimmedSearch);
    //     setPage(1);
    //     fetchServices(0, perPage, trimmedSearch, sortConfig);
    // };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= Math.ceil(total / perPage)) {
            setPage(newPage);
            fetchServices((perPage * (newPage - 1)), perPage, search, sortConfig);
        }
    };

    const handleItemsPerPageChange = (value: number) => {
        setPerPage(value);
        setPage(1);
        fetchServices(0, value, search, sortConfig);
    };

    const handleRefresh = () => {
        const newSearch = "";
        setSearch(newSearch);
        setSearchInput("");
        fetchServices((page - 1) * perPage, perPage, newSearch, sortConfig);
    };

    const handleSort = (key: string) => {
        let newDirection: "asc" | "desc" = "asc";
        if (sortConfig && sortConfig.key === key) {
            newDirection = sortConfig.direction === "asc" ? "desc" : "asc";
        }
        const newSortConfig = { key, direction: newDirection };
        setSortConfig(newSortConfig);
        setPage(1); // Reset to page 1 on sort
        fetchServices(0, perPage, search, newSortConfig);
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey, widthClass }: { label: string; sortKey: string; widthClass?: string }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className={`${widthClass} px-3 py-2 text-left text-[10px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700 cursor-pointer group select-none`}
            >
                <div className="flex items-center gap-1">
                    {label}
                    <div className="flex items-center">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-yellow-400" />
                            ) : (
                                <ChevronDown size={12} className="text-yellow-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-gray-200" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    return (
        <div className="p-3">
            {/* Compact Header */}
            <div className="flex items-center justify-between mb-3 border-b border-gray-700 pb-2">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                        <Server className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-white leading-tight">System Services</h2>
                        <p className="text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {total || '...'} services found
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleRefresh}
                        disabled={loading}
                        className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>
                    <div className="relative">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            placeholder="Search service..."
                            className="w-64 bg-gray-900 border border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500   transition-colors h-8"
                        />
                    </div>
                </div>
            </div>

            {/* Table Container */}
            <div className="bg-gray-900/40 rounded-md border border-gray-700 overflow-hidden">
                <table className="w-full table-fixed">
                    <thead className="bg-gray-800/80">
                        <tr>
                            <SortableHeader widthClass="w-[20%]" label="Service" sortKey="display_name" />
                            <SortableHeader widthClass="w-[8%]" label="Status" sortKey="status" />
                            <SortableHeader widthClass="w-[12%]" label="Account" sortKey="service_account" />
                            <SortableHeader widthClass="w-[18%]" label="Path" sortKey="binary_path" />
                            <SortableHeader widthClass="w-[8%]" label="Start" sortKey="start_type" />
                            <SortableHeader widthClass="w-[8%]" label="Memory" sortKey="memory_usage_mb" />
                            <SortableHeader widthClass="w-[8%]" label="PID" sortKey="process_id" />
                            <SortableHeader widthClass="w-[18%]" label="Process" sortKey="process_name" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                        {loading ? (
                            <tr>
                                <td colSpan={8} className="py-8 text-center text-gray-200 text-[11px]">Loading services...</td>
                            </tr>
                        ) : services.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="py-8 text-center text-gray-200 text-[11px]">No services found</td>
                            </tr>
                        ) : (
                            services.map((svc, idx) => (
                                <tr key={svc.id ?? idx} className="hover:bg-gray-800/40 transition-colors duration-100">
                                    <td className="px-3 py-2">
                                        <div className="text-[11px] font-semibold text-white truncate" title={svc.display_name || svc.name}>
                                            {svc.display_name || svc.name}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${svc.status === "Running"
                                            ? "text-green-400 border-green-500/20 bg-green-500/10"
                                            : "text-gray-200 border-gray-500/20 bg-gray-500/10"
                                            }`}>
                                            {svc.status}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-[10px] text-gray-200 truncate" title={svc.service_account}>{svc.service_account || "-"}</td>
                                    <td className="px-3 py-2 text-[10px]">
                                        <TruncatedPath path={svc.binary_path} />
                                    </td>
                                    <td className="px-3 py-2 text-[10px] text-gray-200 truncate">{svc.start_type || "-"}</td>
                                    <td className="px-3 py-2 text-[10px] text-gray-300 font-mono">{svc.memory_usage_mb ?? "-"} MB</td>
                                    <td className="px-3 py-2 text-[10px] text-gray-200 font-mono">{svc.process_id || "-"}</td>
                                    <td className="px-3 py-2 text-[10px] text-gray-200 truncate" title={svc.process_name}>{svc.process_name || "-"}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    <tfoot className="bg-gray-900/60">
                        <tr>
                            <td colSpan={8} className="p-0">
                                <Pagination
                                    currentPage={page}
                                    totalPages={Math.max(1, Math.ceil(total / perPage))}
                                    onPageChange={handlePageChange}
                                    itemsPerPage={perPage}
                                    totalItems={total}
                                    onItemsPerPageChange={handleItemsPerPageChange}
                                />
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export default ServiceList;